import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Trophy, ShieldCheck, X, BellRing, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const money = (n) => (n == null || n === "" || Number.isNaN(Number(n)) ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const num = (v) => (v === "" || v == null ? null : Number(v));
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => (iso ? new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const plusYear = (iso) => { const d = new Date(iso + "T12:00:00"); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); };
const EMPTY = { insurer: "", agent: "", agent_email: "", liability: "$1M", cargo: "$100k", monthly: "", down: "", notes: "" };

export function InsuranceQuoteTracker({ quotes = [], onChange, renewal, user }) {
  const [draft, setDraft] = useState(EMPTY);
  const [open, setOpen] = useState(quotes.length === 0);
  const [binding, setBinding] = useState(null); // { id, date }
  useEffect(() => { if (quotes.length > 0) setOpen(false); }, [quotes.length]);
  const sorted = [...quotes].sort((a, b) => (a.monthly ?? Infinity) - (b.monthly ?? Infinity));
  const cheapest = sorted.find((q) => q.monthly != null)?.id;
  const bound = quotes.find((q) => q.bound);

  const add = (e) => {
    e.preventDefault();
    if (!draft.insurer.trim() && !draft.agent.trim()) return;
    const q = { id: `q_${Date.now().toString(36)}`, ...draft, insurer: draft.insurer.trim(), agent: draft.agent.trim(), agent_email: draft.agent_email.trim(),
      monthly: num(draft.monthly), down: num(draft.down), added_at: new Date().toISOString(), bound: false, policy_start: "" };
    onChange([...quotes, q]);
    setDraft(EMPTY);
    setOpen(false);
  };
  const remove = (id) => onChange(quotes.filter((q) => q.id !== id));
  const confirmBind = () => {
    if (!binding?.date) return;
    onChange(quotes.map((q) => (q.id === binding.id ? { ...q, bound: true, policy_start: binding.date } : { ...q, bound: false })));
    setBinding(null);
  };
  const unbind = (id) => onChange(quotes.map((q) => (q.id === id ? { ...q, bound: false, policy_start: "" } : q)));
  const inp = "rounded-md bg-black/40 border border-white/10 focus:border-[#FF5A1F] outline-none px-2.5 py-1.5 text-xs text-white placeholder:text-white/25 w-full";

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3 sm:p-4" data-testid="insurance-quote-tracker">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Quote tracker · {quotes.length}/3 logged</div>
        {!open && (
          <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs font-bold text-[#FF5A1F] hover:text-[#FF7038]" data-testid="quote-add-toggle">
            <Plus className="w-3.5 h-3.5" /> Log a quote
          </button>
        )}
      </div>

      {bound && (
        <BoundBanner bound={bound} renewal={renewal} user={user} agentsWithEmail={quotes.filter((q) => (q.agent_email || "").includes("@")).length} />
      )}

      {sorted.length > 0 && (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs min-w-[640px]" data-testid="quote-table">
            <thead className="text-white/40 text-[10px] uppercase tracking-wider">
              <tr>{["Insurer / agent", "Liability", "Cargo", "Monthly", "Down", "Notes", ""].map((h) => <th key={h} className="text-left font-bold px-1 py-1.5">{h}</th>)}</tr>
            </thead>
            <tbody>
              {sorted.map((q) => (
                <QuoteRow key={q.id} q={q} cheapest={q.id === cheapest} dim={!!bound && !q.bound} binding={binding?.id === q.id ? binding : null}
                  onBind={() => setBinding({ id: q.id, date: today() })} onBindDate={(date) => setBinding({ id: q.id, date })}
                  onConfirm={confirmBind} onCancel={() => setBinding(null)} onUnbind={() => unbind(q.id)} onRemove={() => remove(q.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <form onSubmit={add} className="grid grid-cols-2 sm:grid-cols-6 gap-2 mt-3" data-testid="quote-form">
          <input className={`${inp} col-span-2`} placeholder="Insurer (e.g. Progressive)" value={draft.insurer} onChange={(e) => setDraft({ ...draft, insurer: e.target.value })} data-testid="quote-insurer" />
          <input className={`${inp} col-span-2 sm:col-span-1`} placeholder="Agent" value={draft.agent} onChange={(e) => setDraft({ ...draft, agent: e.target.value })} data-testid="quote-agent" />
          <input className={`${inp} col-span-2 sm:col-span-3`} type="email" placeholder="Agent email (for one-tap re-quote requests)" value={draft.agent_email} onChange={(e) => setDraft({ ...draft, agent_email: e.target.value })} data-testid="quote-agent-email" />
          <input className={inp} placeholder="Liability" value={draft.liability} onChange={(e) => setDraft({ ...draft, liability: e.target.value })} data-testid="quote-liability" />
          <input className={inp} placeholder="Cargo" value={draft.cargo} onChange={(e) => setDraft({ ...draft, cargo: e.target.value })} data-testid="quote-cargo" />
          <input className={inp} type="number" min="0" placeholder="$/month" value={draft.monthly} onChange={(e) => setDraft({ ...draft, monthly: e.target.value })} data-testid="quote-monthly" />
          <input className={inp} type="number" min="0" placeholder="Down $" value={draft.down} onChange={(e) => setDraft({ ...draft, down: e.target.value })} data-testid="quote-down" />
          <input className={`${inp} col-span-2 sm:col-span-3`} placeholder="Notes (deductible, filings included, etc.)" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="quote-notes" />
          <div className="col-span-2 flex gap-2 justify-end">
            {quotes.length > 0 && <button type="button" onClick={() => setOpen(false)} className="rounded-full px-3 py-1.5 text-xs text-white/60 hover:text-white">Cancel</button>}
            <button type="submit" className="rounded-full px-4 py-1.5 text-xs font-bold bg-[#FF5A1F] text-black hover:bg-[#FF7038]" data-testid="quote-save">Save quote</button>
          </div>
        </form>
      )}
      {quotes.length >= 3 && !bound && <p className="text-xs text-emerald-400 mt-2" data-testid="quote-done-hint">3 quotes logged — cheapest is highlighted. Compare deductibles and what filings are included, then hit <b>Bind</b> on the winner.</p>}
    </div>
  );
}

function QuoteRow({ q, cheapest, dim, binding, onBind, onBindDate, onConfirm, onCancel, onUnbind, onRemove }) {
  const rowCls = q.bound ? "bg-emerald-500/[0.10]" : cheapest ? "bg-emerald-500/[0.05]" : "";
  return (
    <tr className={`border-t border-white/[0.06] ${rowCls} ${dim ? "opacity-50" : ""}`} data-testid={`quote-row-${q.id}`}>
      <td className="px-1 py-2 text-white">
        <div className="font-semibold flex items-center gap-1.5">
          {q.bound && <span className="text-[9px] font-black tracking-widest rounded-full bg-emerald-500 text-black px-1.5 py-px" data-testid={`quote-bound-badge-${q.id}`}>BOUND</span>}
          {!q.bound && cheapest && <Trophy className="w-3 h-3 text-emerald-400" data-testid="quote-cheapest" />}
          {q.insurer || "—"}
        </div>
        {q.agent && <div className="text-white/45">{q.agent}</div>}
        {q.agent_email && <div className="text-white/35 text-[11px] truncate max-w-[180px]" data-testid={`quote-agent-email-${q.id}`}>{q.agent_email}</div>}
        {q.bound && q.policy_start && <div className="text-emerald-300/80 text-[11px]">starts {fmtDate(q.policy_start)}</div>}
      </td>
      <td className="px-1 py-2 text-white/70">{q.liability || "—"}</td>
      <td className="px-1 py-2 text-white/70">{q.cargo || "—"}</td>
      <td className={`px-1 py-2 font-bold tabular-nums ${cheapest || q.bound ? "text-emerald-300" : "text-white"}`}>{money(q.monthly)}<span className="text-white/35 font-normal">/mo</span></td>
      <td className="px-1 py-2 text-white/70 tabular-nums">{money(q.down)}</td>
      <td className="px-1 py-2 text-white/50 max-w-[140px] truncate">{q.notes}</td>
      <td className="px-1 py-2">
        <div className="flex items-center justify-end gap-2 whitespace-nowrap">
          {binding ? (
            <>
              <input type="date" value={binding.date} onChange={(e) => onBindDate(e.target.value)} className="rounded-md bg-black/40 border border-white/10 px-2 py-1 text-[11px] text-white [color-scheme:dark]" aria-label="Policy start date" data-testid={`quote-bind-date-${q.id}`} />
              <button type="button" onClick={onConfirm} className="rounded-full px-2.5 py-1 text-[11px] font-bold bg-emerald-500 text-black hover:bg-emerald-400" data-testid={`quote-bind-confirm-${q.id}`}>Confirm</button>
              <button type="button" onClick={onCancel} aria-label="Cancel" className="text-white/40 hover:text-white"><X className="w-3.5 h-3.5" /></button>
            </>
          ) : q.bound ? (
            <button type="button" onClick={onUnbind} className="text-[11px] text-white/40 hover:text-white" data-testid={`quote-unbind-${q.id}`}>Unbind</button>
          ) : (
            <button type="button" onClick={onBind} className="rounded-full border border-emerald-500/40 text-emerald-300 px-2.5 py-1 text-[11px] font-bold hover:bg-emerald-500/10" data-testid={`quote-bind-${q.id}`}>Bind</button>
          )}
          <button type="button" onClick={onRemove} aria-label="Remove quote" className="text-white/30 hover:text-red-400 transition-colors" data-testid={`quote-remove-${q.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </td>
    </tr>
  );
}

const agoText = (iso) => {
  const h = (Date.now() - new Date(iso).getTime()) / 3600000;
  return h < 1 ? "just now" : h < 48 ? `${Math.round(h)}h ago` : `${Math.round(h / 24)}d ago`;
};

function BoundBanner({ bound, renewal, user, agentsWithEmail }) {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const lastReq = last || renewal?.requote?.last;

  const requote = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/account/authority-checklist/requote");
      setLast(data);
      toast.success(`Re-quote request emailed to ${data.sent} agent${data.sent === 1 ? "" : "s"} — replies come to your inbox`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send"); }
    finally { setBusy(false); }
  };

  return (
    <div className="text-xs rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-3 py-2 mb-3" data-testid="quote-bound-banner">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" />
        <span>Bound with <b>{bound.insurer || bound.agent}</b> · policy starts <b>{fmtDate(bound.policy_start)}</b> · renews {fmtDate(plusYear(bound.policy_start))}. "Bind insurance" is ticked for you — confirm your BMC-91X shows on FMCSA within 48 hrs.</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-emerald-200/80" data-testid="quote-renewal-reminders">
        <BellRing className="w-3 h-3" />
        {user && renewal ? (
          <span>Re-shop reminders {renewal.reminders.map((r, i) => (
            <span key={r.days_before}>{i > 0 && " · "}<b className="text-emerald-100">{r.days_before}d before</b> ({fmtDate(r.on)}){r.sent_at && " ✓ sent"}</span>
          ))} — texted (email fallback) so you beat the auto-renew.</span>
        ) : user ? (
          <span>Re-shop reminders will text you 90 and 60 days before renewal.</span>
        ) : (
          <span><Link to="/signup" className="underline font-bold">Create a free account</Link> to get texted 90 and 60 days before renewal so you re-shop before the insurer auto-renews.</span>
        )}
      </div>
      {user && (
        <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="quote-requote-row">
          <button type="button" onClick={requote} disabled={busy || agentsWithEmail === 0}
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/50 bg-emerald-500/15 px-3 py-1 text-[11px] font-bold text-emerald-100 hover:bg-emerald-500/25 disabled:opacity-40 transition-colors" data-testid="quote-requote-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Request re-quotes now{agentsWithEmail > 0 ? ` (${agentsWithEmail} agent${agentsWithEmail === 1 ? "" : "s"})` : ""}
          </button>
          <span className="text-emerald-200/70" data-testid="quote-requote-status">
            {agentsWithEmail === 0
              ? "Add an agent email to your quotes to email them all in one tap — we'll also do it automatically when the 90-day reminder fires."
              : lastReq ? `Last sent ${agoText(lastReq.at)} to ${lastReq.sent} agent${lastReq.sent === 1 ? "" : "s"}${lastReq.trigger === "manual" ? "" : " (auto, with your renewal reminder)"}.`
              : "Fires automatically with your 90-day reminder, or tap now."}
          </span>
        </div>
      )}
    </div>
  );
}
